import { DeleteOutlined, DownloadOutlined, InboxOutlined, PhoneOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
  App,
  Button,
  Card,
  Col,
  Collapse,
  Divider,
  InputNumber,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  UploadFile,
} from 'antd';
import Title from 'antd/es/typography/Title';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  removePmOfferSectionFile,
  updateField,
  updatePmOfferSectionFile,
} from '../../../../../contextRedux/PMOfferProject/PmOfferModelProject';
import { RootState } from '../../../../../store';
import request from '../../../../../utils/request';
import './RaportTab.css';

const { Panel } = Collapse;
const { Dragger } = Upload;

const saveBlobCrossPlatform = (blob: Blob, filename: string) => {
  const w = window as any;
  if (w.AndroidInterface && typeof w.AndroidInterface.saveBlobFile === 'function') {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const base64 = (reader.result as string).split(',')[1];
        w.AndroidInterface.saveBlobFile(base64, filename);
      } catch {}
    };
    reader.readAsDataURL(blob);
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 0);
};

const statusConfig = [
  {
    key: 'onHold',

    name: 'On Hold',

    description: 'Lucrarea este temporar suspendata.',

    color: '#f5222d',
  },

  {
    key: 'draft',

    name: 'Draft',

    description: 'Lucrarea este in stadiul de ciorna',

    color: '#722ed1',
  },

  {
    key: 'programata',

    name: 'Programata',

    description:
      'Lucrarea este programata. Data inceperii este confirmata cu beneficiarul. Asteapta doar confirmarea disponibilitatii marfii din depozit.',

    color: '#1890ff',
  },

  {
    key: 'marfaComandataConfirmat',

    name: 'Marfa comandata si termen livrare confirmat',

    description: 'Lucrarea asteapta livrarea marfii.',

    color: '#13c2c2',
  },

  {
    key: 'inExecutie',

    name: 'Lucrare in executie',

    description: 'Lucrarea este in curs de executie.',

    color: '#faad14',
  },

  {
    key: 'lucrareFinalziata',

    name: 'Lucrare finalizata',

    description: 'Lucrarea este finalizata. Asteapta procesarea fisei de instalare.',

    color: '#a0d911',
  },

  {
    key: 'lucrareFinalziataFisa',

    name: 'Lucrare finalizata. Fisa de instalare procesata',

    description: 'Lucrarea este finalizata. Asteapta facturarea',

    color: '#52c41a',
  },

  {
    key: 'lucrareFacutrata',

    name: 'Lucrare facturata. Asteapta feedback',

    description: 'Lucrarea a fost facturata. Asteapta feedback din partea clientului.',

    color: '#237804',
  },

  {
    key: 'lucrareFacutrataFeedback',

    name: 'Lucrare facturata, feedback primit',

    description: 'Lucrarea este facturata, feedback primit.',

    color: '#389e0d',
  },
];

interface TransportDetail {
  distance: number;
  time: number;
  cost: number;
  displacements: number;
  costPerKm: number;
  perTrip: {
    distance: number;
    duration: number;
    cost: number;
    distanceText: string;
    durationText: string;
  };
}

// Add interface for contact person
interface ContactPerson {
  name: string;
  role: string;
  phone: string;
}

// Define document section keys that match the backend model's "section" field
type DocumentSectionKey = 'projectOutline' | 'situationPlan' | 'otherDocuments';

const RaportTab: React.FC = () => {
  const dispatch = useDispatch();
  const { message } = App.useApp();
  const [showLegend, setShowLegend] = useState<boolean>(false);
  const [transportDetails, setTransportDetails] = useState<Record<string, TransportDetail>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [fileLists, setFileLists] = useState<Record<string, UploadFile<any>[]>>({
    projectOutline: [],
    situationPlan: [],
    otherDocuments: [],
  });

  // Define document sections
  const documentSections = [
    { key: 'projectOutline' as DocumentSectionKey, title: 'Schita Proiect' },
    { key: 'situationPlan' as DocumentSectionKey, title: 'Plan Situatie' },
    { key: 'otherDocuments' as DocumentSectionKey, title: 'Alte Documente' },
  ];

  const pmOfferProject = useSelector((state: RootState) => state.models.pmOfferProject);
  const addedDevices = useSelector((state: RootState) => state.models.pmOfferProject.pmOfferWorkPlaceItems);
  const pmOfferSectionFiles = useSelector((state: RootState) => state.models.pmOfferProject.pmOfferSectionFiles || []);

  // Memoize devicesByWorkplace to prevent unnecessary recalculations
  const devicesByWorkplace = useMemo(() => {
    return addedDevices.reduce((acc: any, pmOfferWorkPlaceItem: any) => {
      const workplaceName = pmOfferWorkPlaceItem.workPlace?.name || 'Unknown';
      if (!acc[workplaceName]) {
        acc[workplaceName] = [];
      }
      acc[workplaceName].push(pmOfferWorkPlaceItem);
      return acc;
    }, {});
  }, [addedDevices]);

  // Simplified transport details calculation - just use workplace info without intervention data
  useEffect(() => {
    if (Object.keys(devicesByWorkplace).length === 0) return;

    setLoading(true);
    const details: Record<string, TransportDetail> = {};

    try {
      const workplaces = Object.keys(devicesByWorkplace);
      workplaces.forEach((workplace) => {
        // Create default transport details with 0 values
        details[workplace] = {
          distance: 0,
          time: 0,
          cost: 0,
          displacements: 0,
          costPerKm: pmOfferProject.costPerKilometer || 0,
          perTrip: {
            distance: 0,
            duration: 0,
            cost: 0,
            distanceText: '0 km',
            durationText: '0 ore 0 minute',
          },
        };
      });

      setTransportDetails(details);
    } catch (error) {
      console.error('Error calculating transport details:', error);
      message.error('Eroare la calcularea detaliilor de transport');
    } finally {
      setLoading(false);
    }
  }, [devicesByWorkplace, pmOfferProject.costPerKilometer]);

  // Calculate total costs - simplified without intervention data
  const calculateTotalCosts = () => {
    const devicesCost = addedDevices.reduce(
      (acc, pmOfferWorkPlaceItem) => acc + pmOfferWorkPlaceItem.price + pmOfferWorkPlaceItem.laborPrice,
      0,
    );
    const laborCost =
      pmOfferProject.pmOfferLaborExtras?.reduce(
        (acc, pmOfferlaborExtra) => acc + pmOfferlaborExtra.price * pmOfferlaborExtra.piece,
        0,
      ) || 0;
    // Transport cost will be 0 since we don't have intervention data
    const transportCost = 0;

    return {
      devicesCost,
      laborCost,
      transportCost,
      total: devicesCost + laborCost + transportCost,
    };
  };

  // Helper function to get unique contact persons for a workplace
  const getWorkplaceContacts = (workplaceName: string): ContactPerson[] => {
    const devices = addedDevices.filter(
      (pmOfferWorkPlaceItem) => pmOfferWorkPlaceItem.workPlace?.name === workplaceName,
    );
    const contactsMap = new Map();

    devices.forEach((pmOfferWorkPlaceItem) => {
      if (pmOfferWorkPlaceItem.agent) {
        const key = pmOfferWorkPlaceItem.agent.id;
        contactsMap.set(key, {
          name: pmOfferWorkPlaceItem.agent.name,
          role: pmOfferWorkPlaceItem.agent.role[0],
          phone: pmOfferWorkPlaceItem.agent.phone[0],
        });
      }
    });

    return Array.from(contactsMap.values());
  };

  // Calculate equipment totals including labor time
  const equipmentTotals = useMemo(() => {
    return Object.entries(devicesByWorkplace).map(([workplace, pmOfferWorkPlaceItems]: [string, any]) => {
      const workplaceTotal = pmOfferWorkPlaceItems.reduce(
        (acc: any, pmOfferWorkPlaceItem: any) => ({
          pieces: acc.pieces + pmOfferWorkPlaceItem.piece,
          laborTimeMinutes: acc.laborTimeMinutes + pmOfferWorkPlaceItem.laborTime,
        }),
        { pieces: 0, laborTimeMinutes: 0 },
      );

      return {
        workplace,
        pmOfferWorkPlaceItems: pmOfferWorkPlaceItems.map((pmOfferWorkPlaceItem: any) => ({
          name: pmOfferWorkPlaceItem.device.name,
          pieces: pmOfferWorkPlaceItem.piece,
          laborTimeMinutes: pmOfferWorkPlaceItem.laborTime,
          totalLaborTimeMinutes: pmOfferWorkPlaceItem.laborTime,
        })),
        total: workplaceTotal,
      };
    });
  }, [devicesByWorkplace]);

  // Format minutes to hours and minutes
  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  // Initialize file lists from Redux when component mounts
  useEffect(() => {
    const newFileLists: Record<string, UploadFile<any>[]> = {
      projectOutline: [],
      situationPlan: [],
      otherDocuments: [],
    };

    // Group files by section
    pmOfferSectionFiles.forEach((file: any) => {
      if (file.section && newFileLists[file.section]) {
        newFileLists[file.section].push({
          uid: file.id || file.uid,
          name: file.fileName,
          status: 'done',
          url: file.filePath,
        });
      }
    });

    setFileLists(newFileLists);
  }, [pmOfferSectionFiles]);

  const uploadFiles = async (files: File[]): Promise<any[]> => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    try {
      const response = await request(
        `/pmOffer/upload`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
          },
          body: formData,
        },
        true,
      );

      const data = await response.json();
      if (data?.errors?.length > 0) {
        message.error(data?.errors[0]);
        return [];
      } else {
        message.success(`File uploaded successfully!`);
        return data?.response;
      }
    } catch (error: any) {
      message.error(`Failed to upload files: ${error.message}`);
      return [];
    }
  };

  // Download files function
  const { mutate: downloadFile } = useMutation({
    mutationFn: (formData: any) => request(`/pmOffer/downloadSectionFile`, { method: 'POST', body: formData }),
    onSuccess: (response: any) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: (error: any) => {
      message.error('Eroare procesare request: ' + error.message);
    },
  });

  const handleDownloadFile = async (file: UploadFile) => {
    // Find the file in pmContractSectionFiles
    const sectionFile = pmOfferSectionFiles.find((sf: any) => sf.fileName === file.name || sf.id === file.uid);

    if (sectionFile) {
      const formData = new FormData();
      formData.append('fileName', sectionFile.fileName);
      formData.append('fileData', sectionFile.filePath);
      formData.append('fileType', sectionFile.fileExtension);

      // Call your download function
      downloadFile(formData);
    } else {
      message.error('File not found.');
    }
  };

  // Create upload props for each document section
  const createUploadProps = (sectionKey: DocumentSectionKey) => ({
    multiple: true,
    showUploadList: true,
    fileList: fileLists[sectionKey],
    customRequest: async ({ file, onSuccess, onError }: any) => {
      try {
        const uploadedFiles = await uploadFiles([file]);
        if (uploadedFiles.length > 0) {
          const uploadedFile = {
            ...uploadedFiles[0],
            uid: file.uid,
            id: '', // Set id to null for new files
            fileName: uploadedFiles[0].name || file.name,
            filePath: uploadedFiles[0].url || '',
            fileExtension: file.name.split('.').pop(),
            section: sectionKey,
            pmOfferId: pmOfferProject.id || null,
          };

          // Update local state
          const updatedFileList = [
            ...fileLists[sectionKey],
            {
              uid: file.uid,
              name: uploadedFile.fileName,
              status: 'done',
              url: uploadedFile.filePath,
            },
          ];
          setFileLists((prev: any) => ({ ...prev, [sectionKey]: updatedFileList }));

          // Dispatch to Redux store
          dispatch(updatePmOfferSectionFile(uploadedFile));

          onSuccess('ok');
        } else {
          onError('Upload failed');
        }
      } catch (error) {
        onError(error);
      }
    },
    onRemove: (file: UploadFile) => {
      // Update local state
      const updatedFileList = fileLists[sectionKey].filter((f) => f.uid !== file.uid);
      setFileLists((prev: any) => ({ ...prev, [sectionKey]: updatedFileList }));

      // Dispatch to Redux store
      dispatch(removePmOfferSectionFile(file.uid as string));
    },
    beforeUpload: (file: any) => {
      const isAllowedType = [
        'image/jpg',
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ].includes(file.type);
      if (!isAllowedType) {
        message.error('You can only upload JPG, PNG images or PDF/DOC files!');
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    itemRender: (_originNode: React.ReactNode, file: UploadFile) => (
      <div className='file-actions'>
        <Button type='link' icon={<DownloadOutlined />} onClick={() => handleDownloadFile(file)}>
          {file.name}
        </Button>
        <Tooltip title='Remove'>
          <DeleteOutlined
            onClick={() => {
              // Update local state
              const updatedFileList = fileLists[sectionKey].filter((f) => f.uid !== file.uid);
              setFileLists((prev) => ({ ...prev, [sectionKey]: updatedFileList }));

              // Dispatch to Redux store
              dispatch(removePmOfferSectionFile(file.uid as string));
            }}
          />
        </Tooltip>
      </div>
    ),
  });

  return (
    <div className='raport-container'>
      {/* Status and Project Details Section */}
      <div className='top-section'>
        <Card
          title='Status Proiect'
          className='raport-card status-card'
          // extra={
          //   <Button type='text' icon={<EyeOutlined />} onClick={() => setShowLegend(!showLegend)}>
          //     {showLegend ? 'Ascunde Legenda' : 'Vezi Legenda'}
          //   </Button>
          // }
        >
          <div className='status-content'>
            <div className='current-status'>
              <div className='status-header'>
                <div
                  className='status-indicator'
                  style={{
                    backgroundColor:
                      statusConfig.find((s) => s.name === pmOfferProject.enumPmOfferStatus?.name)?.color || '#d9d9d9',
                  }}
                />
                <div className='status-value'>{pmOfferProject.enumPmOfferStatus?.name || 'Nedefinit'}</div>
              </div>
              <div className='status-description'>
                {statusConfig.find((s) => s.name === pmOfferProject.enumPmOfferStatus?.name)?.description}
              </div>
            </div>

            {showLegend && (
              <div className='status-legend'>
                <div className='legend-grid'>
                  {statusConfig.map((status) => (
                    <div key={status.key} className='legend-item'>
                      <span className='legend-color' style={{ backgroundColor: status.color }} />
                      <div className='legend-content'>
                        <div className='legend-name'>{status.name}</div>
                        <div className='legend-description'>{status.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>

        <Card title='Detalii Proiect' className='raport-card details-card'>
          <div className='project-details'>
            <div className='detail-item'>
              <strong>Beneficiar:</strong> {pmOfferProject.company?.name}
            </div>
            <Divider />
            <div className='detail-item'>
              <strong>Locații și Persoane de Contact:</strong>
              <Table
                dataSource={Object.keys(devicesByWorkplace).flatMap((workplace) => {
                  const contacts = getWorkplaceContacts(workplace);
                  return contacts.length > 0
                    ? contacts.map((contact, index) => ({
                        key: `${workplace}-${index}`,
                        workplace,
                        name: contact.name,
                        role: contact.role,
                        phone: contact.phone,
                      }))
                    : [
                        {
                          key: workplace,
                          workplace,
                          name: '-',
                          role: '-',
                          phone: 'Nu există persoane de contact',
                        },
                      ];
                })}
                columns={[
                  {
                    title: 'Locație',
                    dataIndex: 'workplace',
                    key: 'workplace',
                  },
                  {
                    title: 'Nume Contact',
                    dataIndex: 'name',
                    key: 'name',
                  },
                  {
                    title: 'Rol',
                    dataIndex: 'role',
                    key: 'role',
                  },
                  {
                    title: 'Telefon',
                    dataIndex: 'phone',
                    key: 'phone',
                    render: (phone: string) =>
                      phone === 'Nu există persoane de contact' ? (
                        <span>{phone}</span>
                      ) : (
                        <Tag icon={<PhoneOutlined />}>{phone}</Tag>
                      ),
                  },
                ]}
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Transport and Costs Section */}
      <div className='middle-section'>
        <Card title='Transport (dus - intors)' className='raport-card transport-card'>
          <div className='price-input-container' style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span className='detail-label'>Preț implicit per km:</span>
            <InputNumber
              min={0}
              value={pmOfferProject.costPerKilometer}
              onChange={(value) => dispatch(updateField({ field: 'costPerKilometer', value: value }))}
              style={{ width: '80px' }}
            />
            <span className='detail-unit'>RON</span>
          </div>

          {loading ? (
            <div className='loading-container'>
              <Spin size='large' />
            </div>
          ) : (
            Object.entries(transportDetails).map(([workplace, details]) => {
              return (
                <div key={workplace} className='transport-details'>
                  <h4>
                    {workplace} - {devicesByWorkplace[workplace][0]?.workPlace?.address}
                  </h4>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <div className='detail-label'>Număr deplasări</div>
                      <div className='detail-value'>{details.displacements}</div>
                    </Col>
                    <Col span={24}>
                      <Divider style={{ margin: '12px 0' }} />
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total distanță</div>
                      <div className='detail-value highlight'>{details.distance.toFixed(1)} km</div>
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total timp</div>
                      <div className='detail-value highlight'>{details.perTrip.durationText}</div>
                    </Col>
                    <Col span={8}>
                      <div className='detail-label'>Total cost</div>
                      <div className='detail-value highlight'>{details.cost.toFixed(0)} RON</div>
                    </Col>
                  </Row>
                </div>
              );
            })
          )}
        </Card>

        <Card title='Costuri' className='raport-card costs-card'>
          {(() => {
            const costs = calculateTotalCosts();
            return (
              <div className='costs-content'>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost dispozitive</div>
                      <div className='cost-value'>{costs.devicesCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost extra manoperă</div>
                      <div className='cost-value'>{costs.laborCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className='cost-item'>
                      <div className='cost-label'>Cost total transport</div>
                      <div className='cost-value'>{costs.transportCost.toFixed(2)} RON</div>
                    </div>
                  </Col>
                  <Col span={12} className='total'>
                    <div className='cost-item-total'>
                      <div className='cost-label'>Cost total</div>
                      <div className='cost-value'>{costs.total.toFixed(2)} RON</div>
                    </div>
                  </Col>
                </Row>
              </div>
            );
          })()}
        </Card>
      </div>

      {/* Team Section - Improved to show all technicians without duplicates and excluding chief technician */}
      <Card title='Echipa' className='raport-card'>
        <Table
          bordered
          pagination={false}
          dataSource={Object.keys(devicesByWorkplace).map((workplace) => {
            // Get all devices for this workplace
            const workplaceDevices = devicesByWorkplace[workplace];

            // Find chief technician
            const chiefTechnician =
              workplaceDevices.find((device: any) => device.chiefTehnician)?.chiefTehnician?.name || '-';

            // Get all technicians for this workplace (without duplicates and excluding chief technician)
            const technicians = new Set<string>();
            workplaceDevices.forEach((device: any) => {
              if (device.pmOfferWorkPlaceItemTehnicians && Array.isArray(device.pmOfferWorkPlaceItemTehnicians)) {
                device.pmOfferWorkPlaceItemTehnicians.forEach((tech: any) => {
                  // Only add technician if they're not the chief technician
                  if (tech?.user?.name && tech?.user?.name !== chiefTechnician) {
                    technicians.add(tech?.user?.name);
                  }
                });
              }
            });

            // Convert Set to Array for rendering
            const techniciansList = Array.from(technicians);

            return {
              key: workplace,
              workPlace: workplace,
              chiefTechnician: chiefTechnician,
              technicians: techniciansList,
            };
          })}
          columns={[
            { title: 'Locație', dataIndex: 'workPlace', key: 'workPlace' },
            {
              title: 'Tehnician Șef',
              dataIndex: 'chiefTechnician',
              key: 'chiefTechnician',
              render: (chiefTechnician) =>
                chiefTechnician !== '-' ? <strong>{chiefTechnician}</strong> : <span>Nu este definit</span>,
            },
            {
              title: 'Tehnicieni',
              dataIndex: 'technicians',
              key: 'technicians',
              render: (technicians: string[]) =>
                technicians && technicians.length > 0 ? (
                  <div>
                    {technicians.map((tech, index) => (
                      <Tag key={index} color='blue' style={{ margin: '2px' }}>
                        {tech}
                      </Tag>
                    ))}
                  </div>
                ) : (
                  <span>Nu sunt tehnicieni asignați</span>
                ),
            },
          ]}
          size='middle'
          rowClassName='workplace-row'
          scroll={{ x: 'max-content' }}
        />
      </Card>

      {/* Equipment Section */}
      <Card className='raport-card'>
        {/* Title and Button on the same line */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Title level={5} style={{ margin: 0 }}>
            Echipamente Adaugate
          </Title>
        </div>

        {/* Total General Manoperă (Aligned right) */}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Title level={5} style={{ margin: 0 }}>
            Total timp manoperă:{' '}
            {formatTime(equipmentTotals.reduce((acc, { total }) => acc + total?.laborTimeMinutes, 0))}
          </Title>
        </div>

        <Collapse>
          {equipmentTotals.map(({ workplace, pmOfferWorkPlaceItems: devices, total }) => (
            <Panel
              header={
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <span>{workplace}</span>
                  <Tag color='blue'>Total timp manoperă: {formatTime(total?.laborTimeMinutes)}</Tag>
                </Space>
              }
              key={workplace}
            >
              <Table
                dataSource={devices}
                columns={[
                  {
                    title: 'Dispozitiv',
                    dataIndex: 'name',
                    key: 'name',
                    ellipsis: true,
                    render: (text) => (
                      <Tooltip title={text}>
                        <div
                          style={{
                            maxWidth: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {text}
                        </div>
                      </Tooltip>
                    ),
                  },
                  {
                    title: 'Cantitate',
                    dataIndex: 'pieces',
                    key: 'pieces',
                  },
                  {
                    title: 'Timp Manoperă',
                    dataIndex: 'laborTimeMinutes',
                    key: 'laborTimeMinutes',
                    render: (minutes) => `${minutes} minute`,
                  },
                  {
                    title: 'Total Manoperă',
                    dataIndex: 'totalLaborTimeMinutes',
                    key: 'totalLaborTimeMinutes',
                    render: (minutes) => formatTime(minutes),
                  },
                ]}
                bordered
                pagination={false}
                scroll={{ x: 'max-content' }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{total.pieces}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2}></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{formatTime(total?.laborTimeMinutes)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Panel>
          ))}
        </Collapse>
      </Card>

      {/* Documents Section */}
      <Card title='Documente' className='raport-card'>
        <Collapse>
          {documentSections.map((section) => (
            <Panel header={section.title} key={section.key}>
              <Dragger {...createUploadProps(section.key)}>
                <p className='ant-upload-drag-icon'>
                  <InboxOutlined />
                </p>
                <p className='ant-upload-text'>Click sau trage fisiere pentru incarcare</p>
                <p className='ant-upload-hint'>Supports JPG, PNG, PDF, DOC files.</p>
              </Dragger>
            </Panel>
          ))}
        </Collapse>
      </Card>
    </div>
  );
};

export default RaportTab;
